import React, { useRef, useEffect, useState } from "react";
import PlaySimpleIcon from "../../../assets/svgicons/PlaySimple.svg";
import PauseSimpleIcon from "../../../assets/svgicons/PauseSimple.svg";

interface CustomAudioPlayerProps {
  audioRef: React.RefObject<HTMLAudioElement>;
  messageId: string;
}

const CustomAudioPlayer: React.FC<CustomAudioPlayerProps> = ({ audioRef, messageId }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const progressFillRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlayPause = () => {
      setIsPlaying(!audio.paused);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (progressFillRef.current) {
        let percentage = 0;
        if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
          percentage = (audio.currentTime / audio.duration) * 100;
        } else if (audio.buffered && audio.buffered.length > 0) {
          // For streaming audio, use buffered amount as reference
          const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
          if (bufferedEnd > 0) {
            percentage = (audio.currentTime / bufferedEnd) * 100;
          }
        }
        // Clamp between 0 and 100, but don't force 100% at the end
        progressFillRef.current.style.width = `${Math.max(0, Math.min(percentage, 100))}%`;
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleProgress = () => {
      handleTimeUpdate();
    };

    audio.addEventListener("play", handlePlayPause);
    audio.addEventListener("pause", handlePlayPause);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    // Streamed audio only gets its real duration once the stream ends
    audio.addEventListener("durationchange", handleLoadedMetadata);
    audio.addEventListener("progress", handleProgress);

    return () => {
      audio.removeEventListener("play", handlePlayPause);
      audio.removeEventListener("pause", handlePlayPause);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("durationchange", handleLoadedMetadata);
      audio.removeEventListener("progress", handleProgress);
    };
  }, [audioRef]);

  const formatTime = (time: number) => {
    if (!time || isNaN(time) || !isFinite(time)) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const handlePlayPauseClick = () => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play();
      } else {
        audioRef.current.pause();
      }
    }
  };

  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    // While still streaming, duration is Infinity; fall back to how much is seekable so far
    const seekableEnd = audio.seekable.length > 0 ? audio.seekable.end(audio.seekable.length - 1) : 0;
    const totalTime = isFinite(audio.duration) && audio.duration > 0 ? audio.duration : seekableEnd;
    if (!totalTime) return;
    const rect = progressBarRef.current?.getBoundingClientRect();
    if (!rect) return;
    const percentage = Math.max(0, Math.min((e.clientX - rect.left) / rect.width, 1));
    audio.currentTime = percentage * totalTime;
  };

  return (
    <div className="custom-audio-player">
      <button
        className="play-pause-btn"
        onClick={handlePlayPauseClick}
        title={isPlaying ? "Pause" : "Play"}
      >
        <img
          src={isPlaying ? PauseSimpleIcon : PlaySimpleIcon}
          alt={isPlaying ? "Pause" : "Play"}
        />
      </button>

      <div className="audio-info">
        <div className="time-display">
          {formatTime(currentTime)} {duration && isFinite(duration) ? `/ ${formatTime(duration)}` : ""}
        </div>
        <div
          className="progress-bar-container"
          ref={progressBarRef}
          onClick={handleProgressBarClick}
        >
          <div className="progress-bar">
            <div className="progress-fill" ref={progressFillRef}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomAudioPlayer;
